/**
 * Serviço de Consultas do Sistema MEDICARE
 * Gerencia todas as operações relacionadas a consultas
 */

import { ApiService, buildQueryString } from './api';
import {
  ApiResponse,
  PaginatedResponse,
  Consulta,
  StatusConsulta,
  TipoConsulta,
} from '../types';

// Interface estendida para consulta com dados relacionados
export interface Consultation extends Consulta {
  // Campos adicionais para compatibilidade com a interface atual
  dataHora?: string; // Alias para data_consulta
  data_hora?: string; // Campo retornado pela API
  nome_paciente?: string; // Campo retornado pela API
  motivo?: string; // Alias para observacoes
  valor?: number;
  duracao?: number;
  tipo_consulta?: 'presencial' | 'telemedicina';
  // Novos campos da API
  prompt_usuario?: string;
  resposta_ia?: string;
  prompt_utilizado?: string;
  prompt_utilizado_nome?: string;
  tempo_resposta?: number;
  usuario_nome?: string;
}

// Interface para criar consulta
export interface CriarConsultaData {
  paciente_id: string;
  medico_id: string;
  data_consulta: string;
  tipo: TipoConsulta;
  observacoes?: string;
  valor?: number;
}

// Interface para atualizar consulta
export interface AtualizarConsultaData {
  data_consulta?: string;
  tipo?: TipoConsulta;
  status?: StatusConsulta;
  observacoes?: string;
  diagnostico?: string;
  prescricao?: string;
  duracao?: number;
  valor?: number;
}

// Interface para filtros de consulta
export interface FiltroConsultas {
  paciente_nome?: string;
  medico_nome?: string;
  especialidade?: string;
  status?: StatusConsulta;
  tipo?: TipoConsulta;
  data_inicio?: string;
  data_fim?: string;
  busca?: string;
}

// Função para transformar dados da API para o formato da interface
const transformConsultation = (consulta: any): Consultation => {
  return {
    id: consulta.id,
    paciente_id: consulta.id_usuario,
    medico_id: consulta.id_usuario,
    data_consulta: consulta.data_hora,
    status: consulta.status,
    tipo: 'consulta' as TipoConsulta,
    observacoes: consulta.prompt_usuario || '',
    data_criacao: consulta.data_hora,
    data_atualizacao: consulta.data_hora,
    dataHora: consulta.data_hora,
    data_hora: consulta.data_hora,
    nome_paciente: consulta.nome_paciente,
    motivo: consulta.prompt_usuario || '',
    // Campos específicos da IA
    prompt_usuario: consulta.prompt_usuario,
    resposta_ia: consulta.resposta_ia,
    prompt_utilizado: consulta.prompt_utilizado,
    prompt_utilizado_nome: consulta.prompt_utilizado_nome,
    tempo_resposta: consulta.tempo_resposta,
    usuario_nome: consulta.usuario_nome,
    paciente: {
      id: consulta.id_usuario,
      nome: consulta.nome_paciente || 'Paciente não encontrado',
      email: '',
      tipo: 'paciente' as const,
      tipo_usuario: 'paciente' as const,
      ativo: true,
      data_criacao: '',
      data_atualizacao: ''
    },
    medico: {
      id: consulta.id_usuario,
      nome: consulta.usuario_nome || 'Médico não encontrado',
      email: '',
      tipo: 'medico' as const,
      tipo_usuario: 'medico' as const,
      ativo: true,
      data_criacao: '',
      data_atualizacao: ''
    },
  };
};

export class ConsultationsService {
  private static baseUrl = '/consultas';

  /**
   * Lista consultas com paginação e filtros
   */
  static async listar(
    page: number = 1,
    limit: number = 20,
    filtros?: FiltroConsultas
  ): Promise<{ data: Consultation[]; total: number; totalPages: number }> {
    const params = {
      page,
      limit,
      ...filtros,
    };

    const queryString = buildQueryString(params);
    const response = await ApiService.get<PaginatedResponse<Consulta>>(
      `${this.baseUrl}?${queryString}`
    );

    return {
      data: response.data?.data?.map(transformConsultation) || [],
      total: response.data?.pagination?.total || 0,
      totalPages: response.data?.pagination?.totalPages || 0,
    };
  }

  /**
   * Busca consulta por ID
   */
  static async buscarPorId(id: string): Promise<Consultation> {
    const response = await ApiService.get<Consulta>(`${this.baseUrl}/${id}`);
    if (!response.data) {
      throw new Error('Consulta não encontrada');
    }
    return transformConsultation(response.data);
  }

  /**
   * Cria nova consulta
   */
  static async criar(dados: CriarConsultaData): Promise<Consultation> {
    const response = await ApiService.post<Consulta>(
      this.baseUrl,
      dados
    );
    if (!response.data) {
      throw new Error('Erro ao criar consulta');
    }
    return transformConsultation(response.data);
  }

  /**
   * Atualiza consulta existente
   */
  static async atualizar(
    id: string,
    dados: AtualizarConsultaData
  ): Promise<Consultation> {
    const response = await ApiService.put<Consulta>(
      `${this.baseUrl}/${id}`,
      dados
    );
    if (!response.data) {
      throw new Error('Erro ao atualizar consulta');
    }
    return transformConsultation(response.data);
  }

  /**
   * Deleta consulta
   */
  static async deletar(id: string): Promise<void> {
    await ApiService.delete(`${this.baseUrl}/${id}`);
  }

  /**
   * Atualiza status da consulta
   */
  static async atualizarStatus(
    id: string,
    status: StatusConsulta
  ): Promise<Consultation> {
    const response = await ApiService.patch<Consulta>(
      `${this.baseUrl}/${id}/status`,
      { status }
    );
    if (!response.data) {
      throw new Error('Erro ao atualizar status da consulta');
    }
    return transformConsultation(response.data);
  }

  /**
   * Busca consultas por paciente
   */
  static async buscarPorPaciente(
    pacienteId: string,
    page: number = 1,
    limit: number = 20
  ): Promise<{ data: Consultation[]; total: number; totalPages: number }> {
    const params = { page, limit };
    const queryString = buildQueryString(params);
    
    const response = await ApiService.get<PaginatedResponse<Consulta>>(
      `${this.baseUrl}/paciente/${pacienteId}?${queryString}`
    );

    return {
      data: response.data?.data?.map(transformConsultation) || [],
      total: response.data?.pagination?.total || 0,
      totalPages: response.data?.pagination?.totalPages || 0,
    };
  }

  /**
   * Busca consultas por médico
   */
  static async buscarPorMedico(
    medicoId: string,
    page: number = 1,
    limit: number = 20
  ): Promise<{ data: Consultation[]; total: number; totalPages: number }> {
    const params = { page, limit };
    const queryString = buildQueryString(params);
    
    const response = await ApiService.get<PaginatedResponse<Consulta>>(
      `${this.baseUrl}/medico/${medicoId}?${queryString}`
    );

    return {
      data: response.data?.data?.map(transformConsultation) || [],
      total: response.data?.pagination?.total || 0,
      totalPages: response.data?.pagination?.totalPages || 0,
    };
  }

  /**
   * Busca estatísticas de consultas
   */
  static async buscarEstatisticas(): Promise<any> {
    const response = await ApiService.get(`${this.baseUrl}/estatisticas`);
    return response.data;
  }
}

export default ConsultationsService;